import React, { useState, useRef } from "react";
import api, { formatApiError } from "../lib/api";
import { X, Upload, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function ImportTasksDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(false);
  const inputRef = useRef(null);

  const runPreview = async (f) => {
    setBusy(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/tasks/import?mode=preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    runPreview(f);
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tasks/import?mode=commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} tasks`);
      setCommitted(true);
      setPreview(data);
      onImported && onImported();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 rounded-sm w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#13151A] z-10">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#FF5722]">Bulk Import</div>
            <h2 className="text-xl font-semibold">Import Tasks from Excel</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* File Drop Zone */}
          {!preview && (
            <label
              htmlFor="import-file-input"
              className="flex flex-col items-center justify-center border border-dashed border-white/15 hover:border-[#FF5722]/50 bg-[#0B0C10] rounded-sm p-12 cursor-pointer transition-colors"
            >
              {busy ? (
                <>
                  <Loader2 className="w-8 h-8 text-[#FF5722] animate-spin mb-3" />
                  <span className="text-sm text-slate-300">Analyzing spreadsheet...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-10 h-10 text-slate-500 mb-3" />
                  <span className="text-base text-white font-medium">Select an .xlsx or .xlsm file</span>
                  <span className="mono text-xs text-slate-500 mt-1">
                    Sheet name should be "Activity Data" · header row auto-detected
                  </span>
                  <span className="mono text-[10px] text-slate-600 mt-3 uppercase tracking-widest">
                    Compatible with Sync Master Data 365.xlsm
                  </span>
                </>
              )}
              <input
                id="import-file-input"
                ref={inputRef}
                data-testid="import-file"
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          )}

          {/* Preview Summary */}
          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Ready to Import" value={preview.rows_imported} accent testid="preview-imported" />
                <SummaryCard label="Duplicate (skipped)" value={preview.rows_duplicate} testid="preview-duplicate" />
                <SummaryCard label="Empty Rows" value={preview.rows_skipped_empty} testid="preview-empty" />
                <SummaryCard label="Unmatched Engineers" value={preview.unmatched_engineers} warning={preview.unmatched_engineers > 0} testid="preview-unmatched" />
              </div>

              <div className="border border-white/10 bg-[#0B0C10] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileSpreadsheet className="w-4 h-4 text-[#FF5722]" />
                  <span className="mono text-xs text-slate-300">
                    Sheet: <span className="text-white">{preview.sheet}</span> · Header at row {preview.header_row}
                  </span>
                </div>
                {preview.unmatched_engineers > 0 && !committed && (
                  <div className="flex items-start gap-2 text-xs text-amber-400 mt-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      Some engineer names couldn't be matched — those tasks will be created without an assigned engineer
                      (original name kept in remarks as "[Unmatched engineer: xxx]"). Fix by creating the missing users
                      first, or reassign after import.
                    </div>
                  </div>
                )}
              </div>

              {/* Preview table */}
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 px-4 py-2 border-b border-white/10 bg-[#0B0C10]">
                  Preview (first {preview.preview.length} rows)
                </div>
                <div className="overflow-x-auto max-h-[300px]">
                  <table className="w-full tech-table">
                    <thead>
                      <tr>
                        <th>Serial</th>
                        <th>Date</th>
                        <th>Project</th>
                        <th>Site</th>
                        <th>Circle</th>
                        <th>NE IP</th>
                        <th>Engineer</th>
                        <th>Activity</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((r, i) => (
                        <tr key={i}>
                          <td className="mono text-[#FF5722] text-xs">{r.task_serial}</td>
                          <td className="mono text-xs">{r.date}</td>
                          <td className="text-xs">{r.project_name}</td>
                          <td className="text-xs">{r.site_name || "—"}</td>
                          <td className="mono">{r.circle || "—"}</td>
                          <td className="mono text-xs text-slate-500">{r.ne_ip || "—"}</td>
                          <td className="text-xs">
                            {r.engineer_matched ? (
                              <span className="text-green-400">{r.engineer_name}</span>
                            ) : (
                              <span className="text-amber-400">{r.engineer_name || "—"} <span className="text-[10px] mono">unmatched</span></span>
                            )}
                          </td>
                          <td className="text-xs text-slate-400">
                            {Array.isArray(r.activity_planned) ? r.activity_planned.join(", ") : "—"}
                          </td>
                          <td className="text-xs">{r.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {committed && (
                <div className="border border-green-500/40 bg-green-500/5 p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <div>
                    <div className="text-white font-medium">Import complete</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {preview.inserted} tasks added to the database.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#13151A]">
          {preview && !committed ? (
            <>
              <button
                onClick={() => { setPreview(null); setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                className="border border-white/15 hover:border-white/30 px-4 py-2 text-sm rounded-sm"
                data-testid="import-reset"
              >
                Choose different file
              </button>
              <div className="flex items-center gap-3">
                <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
                <button
                  onClick={commit}
                  disabled={busy || preview.rows_imported === 0}
                  data-testid="import-commit"
                  className="bg-[#FF5722] hover:bg-[#F4511E] disabled:opacity-40 px-5 py-2 text-sm rounded-sm flex items-center gap-2 font-medium"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Confirm & Import {preview.rows_imported} Tasks
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={onClose}
              className="ml-auto border border-white/15 px-4 py-2 text-sm rounded-sm"
              data-testid="import-close"
            >
              {committed ? "Close" : "Cancel"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent, warning, testid }) {
  const color = accent ? "text-[#FF5722]" : warning ? "text-amber-400" : "text-white";
  return (
    <div className="bg-[#0B0C10] border border-white/10 p-4 relative" data-testid={testid}>
      {accent && (
        <>
          <span className="absolute top-0 left-0 w-2 h-[2px] bg-[#FF5722]" />
          <span className="absolute top-0 left-0 h-2 w-[2px] bg-[#FF5722]" />
        </>
      )}
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      <div className={`mono text-2xl font-medium ${color}`}>{value ?? 0}</div>
    </div>
  );
}
